import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";

export default function About() {
  useSEO({
    title: "About Qicino Limited | Nigeria's Leading Software Development Company",
    description: "Discover Qicino Limited's story - Nigeria's premier software development company formerly known as Skills Technologies. We provide custom software, cloud solutions, and digital transformation services across Africa.",
    keywords: "Qicino Limited, about Qicino, software development company Nigeria, Skills Technologies, digital transformation, custom software solutions, technology partner Africa, Qicino mission vision",
    url: "https://qicino.com/about",
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              About <span className="text-primary">Qicino Limited</span>
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Pronounced{" "}
              <span className="font-semibold italic">"Ki Chi No"</span>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              A leading software development company transforming businesses
              through innovative digital solutions and comprehensive technology
              services.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Our Journey
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>Qicino Limited</strong> represents the
                    evolution of excellence in software development. We began
                    our journey as <em>Skills Technologies Nigeria Limited</em>,
                    establishing ourselves as a trusted technology partner for
                    businesses across Nigeria and beyond.
                  </p>
                  <p>
                    As we grew and expanded our capabilities, we recognized the
                    need for a fresh identity that better reflected our vision
                    and expanded scope. Thus,{" "}
                    <strong>Qicino Limited</strong> was born - a name
                    that embodies our commitment to quality, innovation, and
                    client-focused solutions.
                  </p>
                  <p>
                    Today, we stand as more than just a software development
                    company. We are a comprehensive technology ecosystem,
                    serving as an umbrella organization that encompasses
                    multiple business verticals, each dedicated to solving
                    unique challenges in the modern digital landscape.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-2xl">Q</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Qicino Limited
                    </h3>
                    <p className="text-gray-600 italic">
                      Pronounced "Ki Chi No"
                    </p>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">Formerly:</p>
                      <p className="font-medium text-gray-700">
                        Skills Technologies Nigeria Limited
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What We Do */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                What We Do
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Software Development
                  </h3>
                  <p className="text-gray-700">
                    Custom software solutions for small, medium, and large-scale
                    businesses. From web applications to mobile apps and
                    enterprise systems.
                  </p>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Cloud Solutions
                  </h3>
                  <p className="text-gray-700">
                    Comprehensive cloud migration, infrastructure management,
                    and optimization across Azure, AWS, Google Cloud, and Oracle
                    Cloud platforms.
                  </p>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Training & Consulting
                  </h3>
                  <p className="text-gray-700">
                    Professional development programs, technical training, and
                    strategic consulting to empower teams and drive digital
                    transformation.
                  </p>
                </div>
              </div>
            </div>

            {/* Our Ecosystem */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Our Business Ecosystem
              </h2>
              <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                As an umbrella organization, Qicino Limited oversees
                multiple subsidiary companies, each specializing in different
                sectors to provide comprehensive business solutions.
              </p>

              <div className="space-y-8">
                {/* Top: Software Development (Qicino) */}
                <div className="relative max-w-2xl mx-auto pb-12">
                  <div className="text-center p-6 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl border border-purple-200">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 18l6-6-6-6M8 6l-6 6 6 6"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Software Development (Qicino)
                    </h3>
                    <p className="text-gray-700">
                      Our core technology arm delivering bespoke software, web &
                      mobile apps, platforms, and enterprise solutions powering
                      all other business verticals.
                    </p>
                  </div>
                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 bottom-0 w-0.5 h-12 bg-gray-300"></div>
                </div>

                {/* Bottom row: the other three */}
                <div className="relative">
                  <div className="hidden md:block absolute top-0 left-0 right-0 h-0.5 bg-gray-300"></div>
                  <div className="hidden md:block absolute top-0 left-[16.666%] -translate-x-1/2 w-0.5 h-8 bg-gray-300"></div>
                  <div className="hidden md:block absolute top-8 left-[16.666%] -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-gray-300"></div>
                  <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gray-300"></div>
                  <div className="hidden md:block absolute top-8 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-gray-300"></div>
                  <div className="hidden md:block absolute top-0 left-[83.333%] -translate-x-1/2 w-0.5 h-8 bg-gray-300"></div>
                  <div className="hidden md:block absolute top-8 left-[83.333%] -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-gray-300"></div>
                  <div className="grid md:grid-cols-3 gap-8 pt-8">
                    <div className="text-center p-6 bg-gradient-to-br from-green-100 to-green-50 rounded-xl border border-green-200">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Real Estate Services
                      </h3>
                      <p className="text-gray-700">
                        Comprehensive real estate solutions including property
                        management, sales, and digital property platforms.
                      </p>
                    </div>

                    <div className="text-center p-6 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl border border-blue-200">
                      <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Automobile Trading
                      </h3>
                      <p className="text-gray-700">
                        Automotive marketplace solutions, vehicle management
                        systems, and digital trading platforms for the
                        automobile industry.
                      </p>
                    </div>

                    <div className="text-center p-6 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl border border-orange-200">
                      <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Logistics Services
                      </h3>
                      <p className="text-gray-700">
                        Advanced logistics and supply chain management
                        solutions, delivery platforms, and transportation
                        technology systems.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Our Mission
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To empower businesses of all sizes through innovative software
                  solutions, cutting-edge technology, and comprehensive digital
                  services that drive growth, efficiency, and competitive
                  advantage in the digital age.
                </p>
              </div>
              <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To be the leading technology ecosystem in Nigeria and across
                  Africa, recognized for excellence in software development,
                  innovation in business solutions, and our commitment to
                  transforming industries through technology.
                </p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Join the growing number of businesses that trust Qicino Nigeria
                Limited for their software development and digital
                transformation needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/services"
                  className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  Explore Our Services
                </Link>
                <Link
                  to="/contact"
                  className="bg-white text-primary border-2 border-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary/5 transition-colors"
                >
                  Get In Touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
